// features/tour/tourSteps.ts
//
// Per-page guided tours. Instead of one long walkthrough, each relevant page
// has its own short tour that auto-runs the FIRST time a staff member lands on
// it, and can be replayed from the "Take a Tour" menu item.
//
// Page tours run *in place* — they never navigate — because the trigger is the
// user already being on the page. Each step optionally names a `selector`; if
// that element is on the page, the overlay spotlights it, otherwise the card is
// shown centred.

import { ROUTES } from '../../config/routes';

export type TourAudience = 'staff';

/**
 * Which style of tutorial to use for pages that have BOTH:
 *  - 'sandbox'  → a fake interactive practice page (recommended). The real page
 *                 stays quiet; teaching happens on the sandbox, reached from the
 *                 dashboard tour / "Take a Tour".
 *  - 'inplace'  → the original on-the-real-page spotlight walkthrough.
 * Flip this one constant to switch the whole app between the two approaches.
 */
export type TutorialMode = 'sandbox' | 'inplace';
export const TUTORIAL_MODE = 'sandbox' as TutorialMode;

export interface TourStep {
  /** Card heading. */
  title: string;
  /** Body copy. Use \n for paragraph breaks. */
  body: string;
  /** Optional one-line tip rendered in an accent pill under the body. */
  tip?: string;
  /** CSS selector to spotlight if present on the current page. */
  selector?: string;
  /**
   * Optional call-to-action button. When set it replaces the Next/Finish
   * button: clicking it navigates to `route` and ends the current tour (the
   * destination's own page tour then takes over).
   */
  cta?: { label: string; route: string };
}

export interface PageTour {
  /** Stable id — also the key under users/{uid}/toursSeen. */
  id: string;
  /** Human label shown in the tour card header. */
  label: string;
  /** Does this tour apply to the given path? */
  match: (path: string) => boolean;
  steps: TourStep[];
  /** When false, the tour never auto-runs (only via "Take a Tour"). Default true. */
  autoRun?: boolean;
}

const MENU_BTN = '[aria-label="Open navigation menu"]';

/**
 * Build the standard trio for a page that has a practice sandbox:
 *  - a real-page tour (pointer → sandbox in 'sandbox' mode, or an in-place
 *    walkthrough in 'inplace' mode), and
 *  - the sandbox tour itself (its steps + a final "Finish — open <page>" CTA).
 * Returns both so the caller can register them in PAGE_TOURS.
 */
function sandboxPair(opts: {
  idBase: string;
  label: string;
  realMatch: (p: string) => boolean;
  sandboxRoute: string;
  completeRoute: string;
  completeLabel: string;
  pointerBody: string;
  pointerSelector?: string;
  inplaceSteps: TourStep[];
  sandboxSteps: TourStep[];
  finalTitle?: string;
  finalBody?: string;
}): { real: PageTour; sandbox: PageTour } {
  const pointer: PageTour = {
    id: `page-${opts.idBase}-pointer-v1`,
    label: opts.label,
    match: opts.realMatch,
    steps: [{
      title: opts.label,
      body: opts.pointerBody,
      selector: opts.pointerSelector,
      cta: { label: 'Open the practice walkthrough →', route: opts.sandboxRoute },
    }],
  };
  const inplace: PageTour = {
    id: `page-${opts.idBase}-inplace-v1`,
    label: opts.label,
    match: opts.realMatch,
    steps: opts.inplaceSteps,
  };
  const sandbox: PageTour = {
    id: `page-${opts.idBase}-sandbox-v1`,
    label: `${opts.label} walkthrough`,
    match: (p) => p === opts.sandboxRoute,
    steps: [
      ...opts.sandboxSteps,
      {
        title: opts.finalTitle || 'That’s the page! ✅',
        body: opts.finalBody || 'You’ve seen the main controls. Replay any time from “Take a Tour”.',
        cta: { label: opts.completeLabel, route: opts.completeRoute },
      },
    ],
  };
  return { real: TUTORIAL_MODE === 'inplace' ? inplace : pointer, sandbox };
}

// ---------------------------------------------------------------------------
// Dashboard (/landing) — the role dashboard, component by component.
// ---------------------------------------------------------------------------
const DASHBOARD: PageTour = {
  id: 'page-dashboard-v1',
  label: 'Dashboard tour',
  match: (p) => p === '/landing',
  steps: [
    {
      title: 'Welcome — this is your dashboard 👋',
      body: 'Your home base after logging in. It’s tailored to your role and pulls together your events, your team, and anything that needs your attention. Let’s look at each part.\n\nLeave any time with “Skip”; replay later from “Take a Tour” in the menu.',
      selector: '[data-tour="dashboard-root"]',
    },
    {
      title: 'Your tournaments',
      body: 'Every event you’re assigned to, with its live round timer and current round. Tap a tournament to jump straight into Table Management. The small pills are shortcuts: Tables, Deck Check (if you’re on the deck-check team), Floor Teams and Runner.',
      selector: '[data-tour="dash-tournaments"]',
    },
    {
      title: 'Your team',
      body: 'Your floor team at a glance — who’s on it, the languages each person speaks, and whether they’re active or on a break. The team lead is tagged “Lead”.',
      selector: '[data-tour="dash-team"]',
    },
    {
      title: 'Waiting on you',
      body: 'Open table calls across your events and any staff feedback you still owe surface here, each with a shortcut to act on it. When it says “All clear”, you’re caught up.',
      selector: '[data-tour="dash-attention"]',
    },
    {
      title: 'Your break status',
      body: 'Going on a break? Toggle it here so the rest of the floor knows you’re away — and tap “I’m back” when you return. Your status shows on your team’s roster.',
      selector: '[data-tour="dash-break"]',
    },
    {
      title: 'Quick actions',
      body: 'One-tap jumps to the tools you use most — Table Management, Deck Check, Floor Teams and Staff Feedback. Everything here is also in the menu.',
      selector: '[data-tour="dash-quickactions"]',
    },
    {
      title: 'Menu & chat',
      body: 'The menu (top-left) reaches every page. And the chat bubble in the bottom-right opens “How can we help?” — your floor team’s private chat, or a private request to the Scorekeeper or Head Judge.\n\nReady to learn the floor grid? Let’s open a hands-on Table Management walkthrough.',
      selector: MENU_BTN,
      cta: {
        label: 'Show me Table Management →',
        route: TUTORIAL_MODE === 'inplace' ? ROUTES.TABLE_MANAGEMENT_DASHBOARD : ROUTES.TUTORIAL_TABLE,
      },
    },
  ],
};

// ---------------------------------------------------------------------------
// Table Management (/table-management/:id) — the judge's main page. The real
// page may be empty between rounds, so the in-depth, button-by-button teaching
// happens on the sandbox below; here we just orient and link to it.
// ---------------------------------------------------------------------------
// In-place walkthrough on the REAL Table Management page (used when
// TUTORIAL_MODE === 'inplace').
const TABLE_INPLACE: PageTour = {
  id: 'page-table-management-inplace-v1',
  label: 'Table Management',
  match: (p) => p.startsWith('/table-management/'),
  steps: [
    {
      title: 'The live floor grid',
      body: 'This is where you’ll spend most of your time. Every table is a colour-coded card showing who’s playing, who finished, and which tables need attention — the most urgent sort to the top. Tap a card to expand its controls.',
      selector: '[data-tour="tm-grid"]',
    },
    {
      title: 'The round timer',
      body: 'The shared round clock, synced to everyone. It turns amber under 5 minutes, red in the last minute, and shows overtime once it hits zero.',
      selector: '[data-tour="tm-timer"]',
    },
    {
      title: 'Search & filters',
      body: 'Search by table number or player name, and use the legend to filter to one status. “My Team” narrows the grid to your floor team’s table range.',
      selector: '[data-tour="tm-toolbar"]',
    },
    {
      title: 'Table cards',
      body: 'Tap any card to act on it — floor status, no-shows, time extensions, comments and appeals all live on the expanded card.',
      selector: '[data-tour="tm-grid"]',
    },
  ],
};

// Pointer shown on the real page when TUTORIAL_MODE === 'sandbox': on first
// visit it offers the hands-on practice page (instead of the old in-place
// walkthrough), and it's always available via "Take a Tour".
const TABLE_POINTER: PageTour = {
  id: 'page-table-management-pointer-v1',
  label: 'Table Management',
  match: (p) => p.startsWith('/table-management/'),
  steps: [
    {
      title: 'Table Management',
      body: 'This is the live grid for the current round. New here? Open the hands-on practice page to learn every control safely, then come back when you’re ready.',
      selector: '[data-tour="tm-grid"]',
      cta: { label: 'Open the practice walkthrough →', route: ROUTES.TUTORIAL_TABLE },
    },
  ],
};

const TABLE_MANAGEMENT: PageTour = TUTORIAL_MODE === 'inplace' ? TABLE_INPLACE : TABLE_POINTER;

// ---------------------------------------------------------------------------
// Table Management SANDBOX (/tutorial/table-management) — fake data, one card
// permanently expanded, every control explained button-by-button.
// ---------------------------------------------------------------------------
const TUTORIAL_TABLE: PageTour = {
  id: 'page-tutorial-table-v1',
  label: 'Table walkthrough',
  match: (p) => p === ROUTES.TUTORIAL_TABLE,
  steps: [
    {
      title: 'A hands-on practice page',
      body: 'This is a practice Table Management page with sample data — nothing here is live, and it’s fully interactive. Click any card to expand it, and click its controls to open the real dialogs. We’ve expanded a couple of tables already. Let’s go through everything.',
      selector: '[data-tour="ttm-grid"]',
    },
    {
      title: 'The Index',
      body: 'Top-left is the “Index” — it opens the full legend (every colour and icon explained) and lets you filter the grid to a single status. Try opening it.',
      selector: '[data-tour="ttm-index"]',
    },
    {
      title: 'The round timer',
      body: 'The shared round clock, synced to everyone. Tick “Show Timer” to display it; “Timer Functions” starts, pauses and sets it. It turns amber under 5 minutes, red in the last minute, and shows overtime past zero.',
      selector: '[data-tour="ttm-timer"]',
    },
    {
      title: 'The action row',
      body: 'Scorekeeper-level controls live here: “Next Round” archives and advances the round, “Featured” marks a stream/top table, “Penalty” and “Ext. Penalty” issue penalties (to a player or a non-registered person), and “More” holds settings like table offset and closing the tournament.',
      tip: 'Some of these are Scorekeeper-and-above only — Floor Judges won’t see every button.',
      selector: '[data-tour="ttm-actions"]',
    },
    {
      title: 'Search, marked & stats',
      body: 'Search by table number or player name, type missing tables to flag them, and use “Marked”/“Missing” to filter. On the right, the stats show how many tables are in, plus live appeal and missing counts.',
      selector: '[data-tour="ttm-toolbar"]',
    },
    {
      title: 'The grid & its colours',
      body: 'Every table is a colour-coded card and the most urgent sort to the top. Click any card to expand its controls. The cards here show the full colour key — let’s call out the ones people ask about most.',
      selector: '[data-tour="ttm-grid"]',
    },
    {
      title: 'Purple = a missing result',
      body: 'A purple card means the table is still “missing” — its result hasn’t come in yet. Scorekeepers flag tables as missing so the floor knows exactly which matches to chase. They turn purple until the result is received.',
      selector: '[data-tour="ttm-missing"]',
    },
    {
      title: 'Missing-table controls',
      body: 'A purple table shows DIFFERENT controls — only on missing tables. Instead of no-show buttons you get floor statuses (Still playing, No players, Call in Progress) to report what you find, and a “Received” button to clear the table once its result is in. Appeal and Time Extension are here too.',
      tip: 'These floor-status buttons only appear on missing tables — that’s why you won’t see them on a normal card.',
      selector: '[data-tour="ttm-missing-controls"]',
    },
    {
      title: 'Blue = a featured table',
      body: 'A cyan/blue card is a featured table — e.g. a stream or top-table match. Scorekeepers and above set which tables are featured; they sort to the very top so they’re easy to keep an eye on.',
      selector: '[data-tour="ttm-featured"]',
    },
    {
      title: 'The time-extension badge',
      body: 'When a table has extra time, a blue ⏱ badge shows the number of added minutes right on the card (and in the round timer). You’ll grant these from the expanded card in a moment.',
      selector: '[data-tour="ttm-te-badge"]',
    },
    {
      title: 'Yellow & pulsing = a call',
      body: 'A pulsing yellow card means “Call in Progress” — a judge has been called to that table. Other floor statuses you’ll see: green “Still playing”, red “No players”, teal “Draw”, and 🏃 for a dropped player.',
      selector: '[data-tour="ttm-call"]',
    },
    {
      title: 'Player buttons',
      body: 'Each player at the table has a button. Click one now to open that player’s actions: apply a penalty, toggle their feature-match opt-out, drop them from the event, or add a staff comment. (Go ahead — it’s a demo.)',
      tip: 'A staff comment sticks to the player across rounds and reseatings — it shows a 📝 on their button.',
      selector: '[data-tour="ttm-players"]',
    },
    {
      title: 'No-shows — how to handle one',
      body: 'When a player doesn’t turn up, the result still needs recording. The process:\n\n1. Tell the player who IS there to submit the match as a win (a 2-0).\n2. Then mark the player who is NOT there as a no-show using their button below.\n\nA no-show keeps the player in the event (unlike a Drop), so they can come back next round.',
      selector: '[data-tour="ttm-noshow"]',
    },
    {
      title: 'Mark the absent player',
      body: 'Here, Marco Rossi hasn’t shown. So Aiko submits the win, and you tap Marco’s no-show button. His nickname then appears in the table overview, and single vs double no-shows are shown by name. No-shows clear automatically when the next round starts.',
      tip: 'Always no-show the player who is ABSENT — never the one who turned up.',
      selector: '[data-tour="ttm-noshow-absent"]',
    },
    {
      title: 'Mark handled',
      body: 'Once a no-show has been followed up (paperwork done, player spoken to), a Scorekeeper or above marks it “handled” so the rest of the team knows it’s dealt with — and can reopen it if something changes.',
      selector: '[data-tour="ttm-handled"]',
    },
    {
      title: 'Appeal',
      body: 'If a player contests a ruling, click Appeal to open the form and escalate the table to a Head Judge. They’ll take the call, decide, and the outcome is logged. The button turns solid yellow once an appeal is open.',
      selector: '[data-tour="ttm-appeal"]',
    },
    {
      title: 'Time Extension',
      body: 'Click Time Extension to open the dialog — add, subtract, or set the minutes. The extension then shows as a blue ⏱ badge on the card and is reflected in the round clock so everyone’s aware.',
      selector: '[data-tour="ttm-timeext"]',
    },
    {
      title: 'Floor comment',
      body: 'Leave a note on the table itself — e.g. “slow play warning given”, “called for a judge”. It’s attached to this table for the round and turns the card orange so it stands out.',
      selector: '[data-tour="ttm-comment"]',
    },
    {
      title: 'That’s the whole card! ✅',
      body: 'You’ve now seen every control on a table. On a real event this is your main page — tap any card to open these same actions. Replay this walkthrough any time from “Take a Tour”.',
      cta: { label: 'Finish — open Table Management', route: ROUTES.TABLE_MANAGEMENT_DASHBOARD },
    },
  ],
};

// ---------------------------------------------------------------------------
// Deck Check (/deck-check/:id)
// ---------------------------------------------------------------------------
// In-place walkthrough on the REAL Deck Check page (TUTORIAL_MODE === 'inplace').
const DECK_CHECK_INPLACE: PageTour = {
  id: 'page-deck-check-inplace-v1',
  label: 'Deck Check',
  match: (p) => p.startsWith('/deck-check/'),
  steps: [
    {
      title: 'Deck Check station',
      body: 'Verify decklists against the physical deck here. Mark players checked, open game-specific decklist viewers, and issue penalties or time extensions from a table.',
      tip: 'Notes you add to a player persist across rounds — and across the whole series if the event belongs to one.',
    },
  ],
};

// Pointer to the hands-on sandbox (TUTORIAL_MODE === 'sandbox').
const DECK_CHECK_POINTER: PageTour = {
  id: 'page-deck-check-pointer-v1',
  label: 'Deck Check',
  match: (p) => p.startsWith('/deck-check/'),
  steps: [
    {
      title: 'Deck Check',
      body: 'This is the deck-verification station. New here? Open the hands-on practice page to learn every control safely, then come back when you’re ready.',
      cta: { label: 'Open the practice walkthrough →', route: ROUTES.TUTORIAL_DECKCHECK },
    },
  ],
};

const DECK_CHECK: PageTour = TUTORIAL_MODE === 'inplace' ? DECK_CHECK_INPLACE : DECK_CHECK_POINTER;

// Hands-on Deck Check sandbox (/tutorial/deck-check).
const TUTORIAL_DECKCHECK: PageTour = {
  id: 'page-tutorial-deckcheck-v1',
  label: 'Deck Check walkthrough',
  match: (p) => p === ROUTES.TUTORIAL_DECKCHECK,
  steps: [
    {
      title: 'A hands-on Deck Check page',
      body: 'This is a practice Deck Check page with sample data — nothing is live, and the buttons open the real dialogs. Let’s go through how you verify decks during a round.',
      selector: '[data-tour="dc-stats"]',
    },
    {
      title: 'The round timer',
      body: 'Keep an eye on the round clock while you work — deck checks have to fit inside the round.',
      selector: '[data-tour="dc-timer"]',
    },
    {
      title: 'Your progress at a glance',
      body: 'These cards show how much of the round is done: total tables, how many players you’ve checked, and how many are still missing a deck list.',
      selector: '[data-tour="dc-stats"]',
    },
    {
      title: 'Upload deck lists',
      body: 'If players submit lists digitally, upload the JSON here so you can compare the list to the physical deck. Click it to see the upload dialog.',
      selector: '[data-tour="dc-upload"]',
    },
    {
      title: 'Random tables',
      body: 'Let the system pick random tables to inspect — optionally excluding already-checked ones, or (in engine mode) filtering by record. Click to open the generator.',
      selector: '[data-tour="dc-random"]',
    },
    {
      title: 'Request a Head Judge',
      body: 'Found something you’re unsure about? File a request to the Head Judge team with the table and a description. Click to open the form.',
      selector: '[data-tour="dc-appeal"]',
    },
    {
      title: 'Pairings, Players & Standings',
      body: 'Switch between the three tabs: Pairings (tables to check), Players (everyone, with persistent notes), and Standings (rank — handy for sampling top tables).',
      selector: '[data-tour="dc-tabs"]',
    },
    {
      title: 'Find what you need',
      body: 'Search by player or table, and filter by check status — e.g. show only tables that aren’t checked yet.',
      selector: '[data-tour="dc-filters"]',
    },
    {
      title: 'The pairings list',
      body: 'Each row is a table. The colour on the left shows its check status: red = none, yellow = one player, green = both done.',
      selector: '[data-tour="dc-table"]',
    },
    {
      title: 'View the decks',
      body: 'Open both players’ decklists side-by-side to compare against the physical deck. From the viewer you can also issue a penalty. Click to try it.',
      selector: '[data-tour="dc-view"]',
    },
    {
      title: 'Mark checked',
      body: 'Tap to record a player as checked — it cycles so a misclick can be undone (red → one player → both green). The colour updates as you go.',
      selector: '[data-tour="dc-mark"]',
    },
    {
      title: 'Time extension',
      body: 'If your check ate into a player’s time, grant an extension right from the row — add, subtract, or set the minutes.',
      selector: '[data-tour="dc-rowtimeext"]',
    },
    {
      title: 'That’s Deck Check! ✅',
      body: 'You’ve seen the whole station — uploads, random sampling, decklist viewing, marking, penalties, extensions and Head-Judge requests. Replay any time from “Take a Tour”.',
      cta: { label: 'Finish — open Deck Check', route: ROUTES.DECK_CHECK_DASHBOARD },
    },
  ],
};

// ---------------------------------------------------------------------------
// Appeals (/appeals)
// ---------------------------------------------------------------------------
const APPEALS_PAIR = sandboxPair({
  idBase: 'appeals',
  label: 'Appeals',
  realMatch: (p) => p === '/appeals',
  sandboxRoute: ROUTES.TUTORIAL_APPEALS,
  completeRoute: '/appeals',
  completeLabel: 'Finish — open Appeals',
  pointerBody: 'Where Head Judges take and resolve appeal calls. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Appeals', body: 'Floor judges escalate contested rulings here; a Head Judge takes the call, records the outcome, and it’s archived to the log.' },
  ],
  finalTitle: 'Appeals handled! ✅',
  finalBody: 'You’ve seen the per-event tabs, taking a call, concluding and interrupting. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Switch between events', body: 'Each tab is a tournament; a pulsing dot flags events with open appeals waiting.', selector: '[data-tour="ap-tabs"]' },
    { title: 'An appeal', body: 'Yellow = open (waiting for a judge); purple/pulsing = in progress. Deck-check appeals are tagged 🃏.', selector: '[data-tour="ap-card"]' },
    { title: 'Take the call', body: 'Claim an open appeal — it goes in-progress with your name so others know it’s covered. Try it.', selector: '[data-tour="ap-take"]' },
    { title: 'Conclude', body: 'Record the outcome — call Correct, Overruled, or Other. It’s archived to the log and leaves the queue.', selector: '[data-tour="ap-conclude"]' },
    { title: 'Interrupt', body: 'Need to hand it off? Release a call back to the open queue for someone else.', selector: '[data-tour="ap-interrupt"]' },
    { title: 'The log', body: 'Every concluded appeal and how it was ruled lives in the searchable log.', selector: '[data-tour="ap-log"]' },
  ],
});
const APPEALS = APPEALS_PAIR.real;

const STAFFDASH_PAIR = sandboxPair({
  idBase: 'staff-dashboard',
  label: 'Staff Dashboard',
  realMatch: (p) => p === '/staff-dashboard',
  sandboxRoute: ROUTES.TUTORIAL_STAFFDASH,
  completeRoute: '/staff-dashboard',
  completeLabel: 'Finish — open Staff Dashboard',
  pointerBody: 'Your hub for floor teams, assignments and per-event staff. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Staff Dashboard', body: 'See your events at a glance — staff counts, floor teams, who’s on duty — and jump to Floor Teams or Tournament Assignments.' },
  ],
  finalTitle: 'That’s the hub! ✅',
  finalBody: 'You’ve seen the overview, the staff metrics, the deep-dive cards and the event list. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Event overview', body: 'Pick a tournament to see its live staffing at a glance.', selector: '[data-tour="sd-overview"]' },
    { title: 'Staff metrics', body: 'Total assigned, number of floor teams, and how many staff are on duty vs on a break — right now.', selector: '[data-tour="sd-metrics"]' },
    { title: 'Floor teams', body: 'Jump to organising staff into lettered teams with table ranges.', selector: '[data-tour="sd-floorteams"]' },
    { title: 'Tournament assignments', body: 'Jump to assigning staff to per-event roles (Head Judge, Scorekeeper, Floor Judge…).', selector: '[data-tour="sd-assignments"]' },
    { title: 'Your active events', body: 'Each card shows an event’s staff and teams; tap one to focus the overview on it.', selector: '[data-tour="sd-tournaments"]' },
  ],
});
const STAFF_DASH_TOUR = STAFFDASH_PAIR.real;

const TOURNSTAFF_PAIR = sandboxPair({
  idBase: 'tournament-staff',
  label: 'Tournament Assignments',
  realMatch: (p) => p === ROUTES.STAFF_TOURNAMENT_ASSIGNMENTS,
  sandboxRoute: ROUTES.TUTORIAL_TOURNSTAFF,
  completeRoute: ROUTES.STAFF_TOURNAMENT_ASSIGNMENTS,
  completeLabel: 'Finish — open Tournament Assignments',
  pointerBody: 'Assign approved staff to per-event roles. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Tournament Assignments', body: 'Assign staff to roles per tournament, change roles on the fly, add staff, or share role-scoped invite links.' },
  ],
  finalTitle: 'Roster set! ✅',
  finalBody: 'You’ve seen the roster by role, changing a role, adding staff and invite links. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Pick the event', body: 'Each tournament has its own roster — choose which event you’re staffing.', selector: '[data-tour="ta-selector"]' },
    { title: 'Find someone', body: 'Search the roster by name or role.', selector: '[data-tour="ta-filter"]' },
    { title: 'Roster by role', body: 'Staff are grouped by their tournament role, each with a colour dot and a count.', selector: '[data-tour="ta-roster"]' },
    { title: 'Change a role', body: 'Click a person to open a menu and switch their role instantly. Try it.', selector: '[data-tour="ta-chip"]' },
    { title: 'Add staff', body: 'Bulk-assign approved staff who aren’t on this event yet.', selector: '[data-tour="ta-add"]' },
    { title: 'Invite via link', body: 'Generate a role-scoped join link — anyone who opens it is added as that role. Copy it or post to Discord.', selector: '[data-tour="ta-invite"]' },
  ],
});
const TOURN_STAFF_TOUR = TOURNSTAFF_PAIR.real;

const JOBAPPS_PAIR = sandboxPair({
  idBase: 'job-applications',
  label: 'Job Applications',
  realMatch: (p) => p === ROUTES.STORE_APPLICATIONS,
  sandboxRoute: ROUTES.TUTORIAL_JOBAPPS,
  completeRoute: ROUTES.STORE_APPLICATIONS,
  completeLabel: 'Finish — open Job Applications',
  pointerBody: 'Your inbox of applications from the Job Board. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Job Applications', body: 'Review applications staff submitted from the Job Board, then accept (assigning a role) or reject them.' },
  ],
  finalTitle: 'Inbox cleared! ✅',
  finalBody: 'You’ve seen the status stats, filtering, reading an application, and accept/reject. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'At a glance', body: 'See how many applications are pending, accepted and rejected.', selector: '[data-tour="ja-stats"]' },
    { title: 'Filter', body: 'Narrow to one tournament’s applications.', selector: '[data-tour="ja-filter"]' },
    { title: 'By status', body: 'Switch between Pending (to review), Accepted and Rejected.', selector: '[data-tour="ja-tabs"]' },
    { title: 'An application', body: 'Each card shows the applicant, the role they want, and their qualifications and motivation.', selector: '[data-tour="ja-card"]' },
    { title: 'Accept', body: 'Accept an applicant and assign them a role — they’re added to the event’s staff. Try it.', selector: '[data-tour="ja-accept"]' },
    { title: 'Reject', body: 'Decline with an optional note; they move to the Rejected tab.', selector: '[data-tour="ja-reject"]' },
  ],
});
const JOB_APPS_TOUR = JOBAPPS_PAIR.real;

const STOREMGR_PAIR = sandboxPair({
  idBase: 'store-managers',
  label: 'Store Managers',
  realMatch: (p) => p === ROUTES.STORE_MANAGERS,
  sandboxRoute: ROUTES.TUTORIAL_STOREMGR,
  completeRoute: ROUTES.STORE_MANAGERS,
  completeLabel: 'Finish — open Store Managers',
  pointerBody: 'Delegate event management to trusted staff without sharing your login. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Store Managers', body: 'Invite managers via one-time links and grant each of them specific permissions; remove access any time.' },
  ],
  finalTitle: 'Delegation done! ✅',
  finalBody: 'You’ve seen invites, the manager list, per-manager permissions and removal. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Invite a manager', body: 'Generate a single-use link (expires in 24h) for a new manager. Try it — it’s copied for you.', selector: '[data-tour="sm-newinvite"]' },
    { title: 'Outstanding invites', body: 'Copy or revoke links that haven’t been used yet.', selector: '[data-tour="sm-invite"]' },
    { title: 'Your managers', body: 'Everyone who’s accepted, with how many permissions they hold.', selector: '[data-tour="sm-managers"]' },
    { title: 'Fine-grained permissions', body: 'Toggle exactly what each manager can do — create tournaments, manage series, or (carefully) manage other managers. Changes apply instantly.', selector: '[data-tour="sm-perms"]' },
    { title: 'Remove access', body: 'Revoke a manager entirely — they keep their own staff account but lose store delegation.', selector: '[data-tour="sm-remove"]' },
  ],
});
const STORE_MGR_TOUR = STOREMGR_PAIR.real;

// Player dashboard — sandbox-only (players reach it via their nav launcher).
const PLAYER_TOUR: PageTour = {
  id: 'page-tutorial-player-v1',
  label: 'Player Dashboard walkthrough',
  match: (p) => p === ROUTES.TUTORIAL_PLAYER,
  steps: [
    { title: 'Welcome to your dashboard 👋', body: 'This is your home base — your tournaments, decks and results. Let’s take a quick look. Nothing here is live.', selector: '[data-tour="pd-welcome"]' },
    { title: 'Your decks', body: 'Build and store decklists, then submit one to an event that requires a list.', selector: '[data-tour="pd-decks"]' },
    { title: 'Active tournaments', body: 'Events you’re playing right now. Tap one to see your pairing, the round timer and standings.', selector: '[data-tour="pd-active"]' },
    { title: 'A tournament card', body: 'Shows the status, round, your win/loss record, and whether your deck is submitted (green) or still needed (orange).', selector: '[data-tour="pd-card"]' },
    { title: 'Your history', body: 'Past tournaments and your final results live here.', selector: '[data-tour="pd-past"]' },
    { title: 'You’re ready to play! ✅', body: 'That’s the portal. Turn on notifications in Settings so you’re alerted when your round is paired. Replay any time from the menu.', cta: { label: 'Finish — go to my dashboard', route: ROUTES.PLAYER_DASHBOARD } },
  ],
};

const MATCHMAKING_PAIR = sandboxPair({
  idBase: 'matchmaking',
  label: 'Matchmaking Runner',
  realMatch: (p) => /^\/tournament\/[^/]+\/matchmaking$/.test(p),
  sandboxRoute: ROUTES.TUTORIAL_MATCHMAKING,
  completeRoute: ROUTES.MATCHMAKING_DASHBOARD,
  completeLabel: 'Finish — open Matchmaking',
  pointerBody: 'The operator console for on-demand, queue-based events. New here? Open the hands-on practice page first.',
  inplaceSteps: [
    { title: 'Matchmaking Runner', body: 'Unlike a fixed-round event, players queue on-demand, get paired by record, play a short session, then it closes. Manage the queue, live matches, sessions, players and settings from the tabs.' },
  ],
  finalTitle: 'You can run matchmaking! ✅',
  finalBody: 'You’ve seen the queue, live matches, sessions, player registration and settings. Replay any time from “Take a Tour”.',
  // Step order must match STEP_TAB in TutorialMatchmakingPage.tsx (the tour
  // drives the active tab as it advances).
  sandboxSteps: [
    { title: 'How matchmaking works', body: 'This is a continuous, queue-based event: players join the queue, get paired against someone with a similar record, play a short session (e.g. 4 rounds), then the session closes. The five tabs are your console.', selector: '[data-tour="mm-tabs"]' },
    { title: 'The waiting queue', body: 'Everyone waiting to be paired, with their record, how long they’ve waited and when they’ll time out. Remove a no-show with the red icon.', selector: '[data-tour="mm-queue"]' },
    { title: 'Force re-pair', body: 'The matcher runs automatically, but you can nudge it to pair the queue right now — handy when tables free up.', selector: '[data-tour="mm-repair"]' },
    { title: 'Table capacity', body: 'On the Live tab: every table at a glance — green is free, orange is occupied. Hover to see who’s playing.', selector: '[data-tour="mm-tables"]' },
    { title: 'Enter results', body: 'Each live match shows a prep/match/ended timer. When a match finishes, click Enter to record the winner and game score. Try it.', selector: '[data-tour="mm-enter"]' },
    { title: 'Sessions & history', body: 'The Sessions tab groups every player’s rounds. Expand one to see each opponent, table and result — and fix a result if needed.', selector: '[data-tour="mm-sessions"]' },
    { title: 'Register a walk-up', body: 'On the Players tab, register someone by their Konami/Bandai ID. No account? A guest is created and you’re handed login codes for them.', selector: '[data-tour="mm-register"]' },
    { title: 'The roster', body: 'Everyone registered, whether they’re a real account or a guest, and how many sessions they’ve played. Re-show guest codes with the key icon.', selector: '[data-tour="mm-roster"]' },
    { title: 'Event settings', body: 'Tune the tier label, table range, rounds per session, queue timeout, best-of and whether to pair by record — all on the Settings tab.', selector: '[data-tour="mm-settings"]' },
    { title: 'Pause the matcher', body: 'Need a breather? Pause matching — players can still queue, but no new pairings are made until you resume.', selector: '[data-tour="mm-pause"]' },
    { title: 'Closing the event', body: 'When you’re done, the danger zone archives the tournament and removes it from active dashboards. It asks you to confirm first.', selector: '[data-tour="mm-close"]' },
  ],
});
const MATCHMAKING_TOUR = MATCHMAKING_PAIR.real;

const RUNNER_PAIR = sandboxPair({
  idBase: 'tournament-runner',
  label: 'Tournament Runner',
  realMatch: (p) => /^\/tournament\/[^/]+\/run$/.test(p),
  sandboxRoute: ROUTES.TUTORIAL_RUNNER,
  completeRoute: ROUTES.TOURNAMENT_DASHBOARD,
  completeLabel: 'Finish — open the dashboard',
  pointerBody: 'The full operator console for an engine-mode event — pairings, standings, players and the top-cut bracket. New here? Open the hands-on practice page first.',
  inplaceSteps: [
    { title: 'Tournament Runner', body: 'RoundMaster pairs each round for you. From the four tabs you run pairings, watch standings, manage players and (after the cut) the bracket — with round controls in the header.' },
  ],
  finalTitle: 'You can run an event! ✅',
  finalBody: 'You’ve seen the round controls, pairings & results, standings, players and the bracket. Replay any time from “Take a Tour”.',
  // Step order must match STEP_TAB in TutorialTournamentRunnerPage.tsx.
  sandboxSteps: [
    { title: 'Welcome to the Runner', body: 'This is the console for an engine-mode (RoundMaster-paired) event. The header shows the name, status and your round controls; the four tabs hold the detail.', selector: '[data-tour="tr-header"]' },
    { title: 'The four tabs', body: 'Pairings (the current round), Standings (the leaderboard), Players (the roster), and Bracket (single-elimination, once the top cut starts). The tour will move through them for you.', selector: '[data-tour="tr-tabs"]' },
    { title: 'Start the tournament', body: 'From registration, “Start Tournament” pairs Round 1 from your registered players. (In the real app the round buttons appear one at a time as the round progresses — here they’re shown together so you can see them all.)', selector: '[data-tour="tr-start"]' },
    { title: 'The pairings', body: 'Every table for the round, with its two players and the result. ⭐ marks a featured table; the result chip is yellow until a score is entered.', selector: '[data-tour="tr-pairings"]' },
    { title: 'Enter a result', body: 'Click Enter on a table to record the winner and game score. The chip turns green and standings recompute. Try it.', selector: '[data-tour="tr-enter"]' },
    { title: 'Standings', body: 'The live leaderboard — record, match points and tiebreakers (OMW%). Players in the top-cut zone are highlighted.', selector: '[data-tour="tr-standings"]' },
    { title: 'Players', body: 'The roster — edit a player, drop someone who left (or undrop them), and manage guest credentials for late registrations.', selector: '[data-tour="tr-players"]' },
    { title: 'Complete the round', body: 'Once every result is in, “Complete Round” locks it. The counter shows how many are still pending.', selector: '[data-tour="tr-complete"]' },
    { title: 'Generate the next round', body: 'RoundMaster pairs the next Swiss round automatically — same-record players, avoiding rematches.', selector: '[data-tour="tr-generate"]' },
    { title: 'Start the top cut', body: 'When Swiss is done, “Start Top Cut” converts the standings into a single-elimination bracket (Top 4/8/16…).', selector: '[data-tour="tr-topcut"]' },
    { title: 'The bracket', body: 'The elimination bracket — Quarterfinals through the Final. Click a match to enter its result; the winner advances automatically.', selector: '[data-tour="tr-bracket"]' },
    { title: 'More tools & ending', body: 'The ⋮ menu holds power tools — export to KTS, convert no-shows, regenerate a round — and “End Tournament”, which archives the event and returns you to the dashboard.', selector: '[data-tour="tr-more"]' },
  ],
});
const RUNNER_TOUR = RUNNER_PAIR.real;

// ---------------------------------------------------------------------------
// Timer Overview (/timer-overview)
// ---------------------------------------------------------------------------
const TIMER_PAIR = sandboxPair({
  idBase: 'timer-overview',
  label: 'Timer Overview',
  realMatch: (p) => p === '/timer-overview',
  sandboxRoute: ROUTES.TUTORIAL_TIMER,
  completeRoute: ROUTES.TIMER_OVERVIEW,
  completeLabel: 'Finish — open Timer Overview',
  pointerBody: 'Every event you’re assigned to, each with its live round clock on one board. New here? Open the hands-on practice page first.',
  inplaceSteps: [
    { title: 'Timer Overview', body: 'Every event you’re assigned to with its live round clock on one board. Reorder the cards, minimise the ones you’re half-watching, or pop a tournament’s working surface onto a second screen.' },
  ],
  finalTitle: 'You can run the clocks! ✅',
  finalBody: 'You’ve seen the board, the per-event timer, its controls and the open-in-new-window button. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Your tournament timers', body: 'Each card is one event you’re assigned to, with its own round clock. Admins see every event; everyone else sees just theirs.', selector: '[data-tour="tt-grid"]' },
    { title: 'Rearrange the board', body: 'Drag a card by its ⋮⋮ handle to reorder — your layout is saved to this browser so your most-watched events stay on top.', selector: '[data-tour="tt-drag"]' },
    { title: 'Minimise the quiet ones', body: 'Collapse a card you’re only half-watching to a compact strip, and expand it again when it heats up. Try the toggle.', selector: '[data-tour="tt-minimize"]' },
    { title: 'The round timer', body: 'The shared clock for that event — green normally, red in the last minute and overtime. Everyone sees the same time.', selector: '[data-tour="tt-widget"]' },
    { title: 'Timer functions', body: 'Open the controls to set the duration and colour, add a seating-phase timer, and start, pause or reset the clock. (Scorekeeper-and-above.)', selector: '[data-tour="tt-controls"]' },
    { title: 'Open on a second screen', body: 'Pop the event’s working surface (Tournament Runner or Table Management) into a new window — handy for a second monitor.', selector: '[data-tour="tt-open"]' },
  ],
});
const TIMER_OVERVIEW = TIMER_PAIR.real;

const JOBS_PAIR = sandboxPair({
  idBase: 'job-board',
  label: 'Job Board',
  realMatch: (p) => p === ROUTES.JOB_BOARD,
  sandboxRoute: ROUTES.TUTORIAL_JOBS,
  completeRoute: ROUTES.JOB_BOARD,
  completeLabel: 'Finish — open Job Board',
  pointerBody: 'Tournaments looking for staff. New here? Open the practice page to see how to browse and apply.',
  inplaceSteps: [
    { title: 'Job Board', body: 'Browse tournaments that need staff, filter by country/city/role, and apply to the ones that fit.' },
  ],
  finalTitle: 'Go get staffed! ✅',
  finalBody: 'You’ve seen how to filter, read a posting and apply. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Find work that fits', body: 'Search by event, store or city and filter by country and role to narrow the board down to jobs you want.', selector: '[data-tour="jb-filters"]' },
    { title: 'Clear the filters', body: 'Reset everything with one tap to see the whole board again.', selector: '[data-tour="jb-clear"]' },
    { title: 'A job posting', body: 'Each card shows the store, event, dates, location, pay (if offered) and the roles still needed.', selector: '[data-tour="jb-card"]' },
    { title: 'View & apply', body: 'Open a posting to read the full details and apply. Click it to see the application form.', selector: '[data-tour="jb-apply"]' },
    { title: 'The application', body: 'Pick the role you want, describe your experience and why you’re interested, and add your availability.', selector: '[data-tour="jb-form"]' },
    { title: 'Submit', body: 'Send your application — the store sees it in their Job Applications inbox. (Nothing is sent here, it’s a demo.)', selector: '[data-tour="jb-submit"]' },
  ],
});
const JOB_BOARD_TOUR = JOBS_PAIR.real;

const DQ_PAIR = sandboxPair({
  idBase: 'dq-cases',
  label: 'DQ Cases',
  realMatch: (p) => p === ROUTES.DQ_CASES,
  sandboxRoute: ROUTES.TUTORIAL_DQ,
  completeRoute: ROUTES.DQ_CASES,
  completeLabel: 'Finish — open DQ Cases',
  pointerBody: 'Where serious incidents are documented as official, auditable cases. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'DQ Cases', body: 'Open a case for a serious incident, collect staff and witness statements, and keep an auditable record.' },
  ],
  finalTitle: 'That’s DQ Cases! ✅',
  finalBody: 'You’ve seen the case list, statuses, form progress and how a new case starts. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Your cases', body: 'Every disqualification case you can see, with the offender, tournament, status and how many statement forms are in.', selector: '[data-tour="dq-table"]' },
    { title: 'Open vs closed', body: 'An “open” case can still take statements and edits; “closed” cases are locked but stay readable for audit.', selector: '[data-tour="dq-status"]' },
    { title: 'Form progress', body: 'This shows how many assigned statements are submitted vs still pending — green once everyone’s in.', selector: '[data-tour="dq-forms"]' },
    { title: 'Open a new case', body: 'Start a case here: pick the tournament, enter the offender, add their contact details, then assign staff and generate QR forms for witnesses. Click to preview the form.', selector: '[data-tour="dq-new"]' },
  ],
});
const DQ_CASES_TOUR = DQ_PAIR.real;

const PENALTY_PAIR = sandboxPair({
  idBase: 'penalty-approval',
  label: 'Penalty Approval',
  realMatch: (p) => p === ROUTES.PENALTY_APPROVAL,
  sandboxRoute: ROUTES.TUTORIAL_PENALTY,
  completeRoute: ROUTES.PENALTY_APPROVAL,
  completeLabel: 'Finish — open Penalty Approval',
  pointerBody: 'The queue where senior staff rule on pending penalties. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Penalty Approval', body: 'Review pending penalties per event and Approve, Reject, or rule on an upgrade. You only see the levels you’re allowed to sign off.' },
  ],
  finalTitle: 'You can clear the queue! ✅',
  finalBody: 'You’ve seen the per-event tabs, the penalty card, the required level, and the approve / reject / upgrade flows. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Switch between events', body: 'Each tab is a tournament with pending penalties — a pulsing dot means that event needs attention.', selector: '[data-tour="pa-tabs"]' },
    { title: 'A penalty to rule on', body: 'Each card shows the player, the infraction and severity, the table, and when it was issued. External (non-registered) people are tagged too.', selector: '[data-tour="pa-card"]' },
    { title: 'Who has to sign off', body: 'The “Required approval” line tells you the level needed — Team Lead for game-loss penalties, Head Judge for match-loss, DQ and upgrades.', selector: '[data-tour="pa-level"]' },
    { title: 'Approve', body: 'If the call is sound, approve it — it’s applied immediately and leaves the queue. Try it.', selector: '[data-tour="pa-approve"]' },
    { title: 'Reject (with a reason)', body: 'Rejecting opens an editor so you can fix the type or note why before it’s recorded as rejected.', selector: '[data-tour="pa-reject"]' },
    { title: 'Upgrades', body: 'A repeat infraction can come in as an upgrade (red card). Approving lets you set the applied penalty; rejecting drops the upgrade request.', selector: '[data-tour="pa-upgrade"]' },
  ],
});
const PENALTY_TOUR = PENALTY_PAIR.real;

const FEEDBACK_PAIR = sandboxPair({
  idBase: 'staff-feedback',
  label: 'Staff Feedback',
  realMatch: (p) => p === ROUTES.STAFF_FEEDBACK,
  sandboxRoute: ROUTES.TUTORIAL_FEEDBACK,
  completeRoute: ROUTES.STAFF_FEEDBACK,
  completeLabel: 'Finish — open Staff Feedback',
  pointerBody: 'Where you give anonymous feedback on the staff you worked with. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Staff Feedback', body: 'Give anonymous feedback on staff. Team Leads review their floor; Head Judges review Team Leads. You can also rate anyone voluntarily.' },
  ],
  finalTitle: 'Thanks for the feedback! ✅',
  finalBody: 'You’ve seen the series scope, your required reviews, the rating form and voluntary feedback. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Pick the series', body: 'Feedback is scoped to a tournament series — choose which one’s staff you’re reviewing.', selector: '[data-tour="sf-series"]' },
    { title: 'Your required reviews', body: 'If you’re a Team Lead or Head Judge, the people you must review are listed here with a “to do” count. A ✓ means you’re done.', selector: '[data-tour="sf-required"]' },
    { title: 'Give feedback', body: 'Open the form for a person. Click it to see the rating form.', selector: '[data-tour="sf-give"]' },
    { title: 'Rate five dimensions', body: 'Score Knowledge (rulings & policy), player interaction, communication and positivity from 1–10.', selector: '[data-tour="sf-metrics"]' },
    { title: 'Didn’t interact?', body: 'If you didn’t work with them enough to rate fairly, flip this — it dims the scores and you can just leave a note.', selector: '[data-tour="sf-nointeract"]' },
    { title: 'Add a written note', body: 'Optional free-text for strengths, areas to improve, or context for the admin who reviews it.', selector: '[data-tour="sf-comment"]' },
    { title: 'Feedback on anyone', body: 'Beyond your required list, you can voluntarily rate any staff member in the series.', selector: '[data-tour="sf-anyone"]' },
  ],
});
const FEEDBACK_TOUR = FEEDBACK_PAIR.real;

const ROUNDARCHIVE_PAIR = sandboxPair({
  idBase: 'round-archive',
  label: 'Round Archive',
  realMatch: (p) => p.startsWith('/round-archive/'),
  sandboxRoute: ROUTES.TUTORIAL_ROUNDARCHIVE,
  completeRoute: ROUTES.TOURNAMENT_DASHBOARD,
  completeLabel: 'Finish — open the dashboard',
  pointerBody: 'A read-only look back at every archived round. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Round Archive', body: 'Browse archived rounds for a tournament — open any round to see its tables, statuses and comments.' },
  ],
  finalTitle: 'That’s the archive! ✅',
  finalBody: 'You’ve seen how to open a past round, read the legend, filter tables and (for managers) prune old data. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Past tournaments', body: 'Each card is a tournament with its table range and a button for every archived round.', selector: '[data-tour="ra-card"]' },
    { title: 'Open a round', body: 'Click a round to open its archived snapshot — the full grid as it was at the time. Try it.', selector: '[data-tour="ra-round"]' },
    { title: 'The legend', body: 'Colours and icons mean the same as on the live floor: green = still playing, yellow = draw/call, orange/red = no-shows and drops.', selector: '[data-tour="ra-legend"]' },
    { title: 'Filter the grid', body: 'Find a specific table by number or comment inside the archived round.', selector: '[data-tour="ra-filter"]' },
    { title: 'Prune old data', body: 'Managers, Scorekeepers and admins can delete an archived round — handy for clearing orphaned data. It asks for confirmation first.', selector: '[data-tour="ra-delete"]' },
  ],
});
const ROUNDARCHIVE_TOUR = ROUNDARCHIVE_PAIR.real;

// ---------------------------------------------------------------------------
// Floor Teams (/staff-floor-teams)
// ---------------------------------------------------------------------------
const FLOORTEAMS_PAIR = sandboxPair({
  idBase: 'floor-teams',
  label: 'Floor Teams',
  realMatch: (p) => p === '/staff-floor-teams',
  sandboxRoute: ROUTES.TUTORIAL_FLOORTEAMS,
  completeRoute: '/staff-floor-teams',
  completeLabel: 'Finish — open Floor Teams',
  pointerBody: 'Where staff are organised into lettered teams with table ranges. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Floor Teams', body: 'Organise staff into lettered teams with table ranges, plus special Head Judge / Feature Match / Deck Check teams. Search the unassigned pool and tap “Assign”.' },
  ],
  finalTitle: 'Teams sorted! ✅',
  finalBody: 'You’ve seen the team cards, the unassigned pool with search + one-tap assign, and creating a team. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Pick the tournament', body: 'Floor teams are per event — choose which tournament you’re staffing, and create new teams with “New team”.', selector: '[data-tour="ft-selector"]' },
    { title: 'Create a team', body: 'Add a lettered team (A, B, C…) with a table range, or flag it as the Head Judge / Feature Match / Deck Check team. Click to see the dialog.', selector: '[data-tour="ft-newteam"]' },
    { title: 'A team card', body: 'Each card shows the team letter, its table range, the members (with the lead starred) and how many are on break. Click the chevron to expand it.', selector: '[data-tour="ft-card"]' },
    { title: 'Team members', body: 'Inside, each person shows their role, languages and on-duty/break status. From here managers set the team lead, move people, or remove them.', selector: '[data-tour="ft-members"]' },
    { title: 'The unassigned pool', body: 'Everyone not yet on a team sits here. For big rosters, search by name or role instead of scrolling.', selector: '[data-tour="ft-pool"]' },
    { title: 'One-tap assign', body: 'Tap “Assign” on a person and pick a team — no dragging needed (drag still works). Try it.', selector: '[data-tour="ft-assign"]' },
  ],
});
const FLOOR_TEAMS = FLOORTEAMS_PAIR.real;

const SERIES_PAIR = sandboxPair({
  idBase: 'tournament-series',
  label: 'Tournament Series',
  realMatch: (p) => p === ROUTES.TOURNAMENT_SERIES,
  sandboxRoute: ROUTES.TUTORIAL_SERIES,
  completeRoute: ROUTES.TOURNAMENT_SERIES,
  completeLabel: 'Finish — open Tournament Series',
  pointerBody: 'Group recurring events under one shared table pool and staff roster. New here? Open the practice page first.',
  inplaceSteps: [
    { title: 'Tournament Series', body: 'A series owns the physical tables for a recurring event; tournaments inside reserve ranges and share staff. Open one to manage capacity, attach tournaments and build the staff pool.' },
  ],
  finalTitle: 'That’s Series! ✅',
  finalBody: 'You’ve seen the series list, statuses, the shared table pool, and creating a series. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Your series', body: 'Each row is a series with its status, how many of its tables are reserved, and how many tournaments and staff it holds.', selector: '[data-tour="ts-table"]' },
    { title: 'Status', body: 'A series moves through planning → active → closed. Only active series track live floor activity.', selector: '[data-tour="ts-status"]' },
    { title: 'The shared table pool', body: 'The bar shows reserved vs total tables. Tournaments inside the series draw their table ranges from this one pool, so they never clash.', selector: '[data-tour="ts-tables"]' },
    { title: 'Create a series', body: 'Start a new series with a name and your venue’s total table count. Click to see the form.', selector: '[data-tour="ts-new"]' },
  ],
});
const SERIES_TOUR = SERIES_PAIR.real;

const CREATE_PAIR = sandboxPair({
  idBase: 'create-tournament',
  label: 'Create Tournament',
  realMatch: (p) => p === ROUTES.TOURNAMENT_SETUP,
  sandboxRoute: ROUTES.TUTORIAL_CREATE,
  completeRoute: ROUTES.TOURNAMENT_SETUP,
  completeLabel: 'Finish — open Create Tournament',
  pointerBody: 'The setup form for a new event. New here? Open the practice page to walk through it first.',
  inplaceSteps: [
    { title: 'Create Tournament', body: 'Name the event, pick the game, choose a mode (engine / matchmaking / manual), set the format and rounds, and optionally attach a series or post to the job board.' },
  ],
  finalTitle: 'Ready to run an event! ✅',
  finalBody: 'You’ve seen the basics, the three modes, the engine settings, series attachment and job-board posting. Replay any time from “Take a Tour”.',
  sandboxSteps: [
    { title: 'Name & game', body: 'Start with a name and the card game — RoundMaster adapts IDs, penalties and tiebreakers to Konami or Bandai.', selector: '[data-tour="tts-basicinfo"]' },
    { title: 'Attach a series', body: 'Optionally put the event inside a tournament series to share tables and staff. Scorekeepers must pick a series they’re assigned to.', selector: '[data-tour="tts-series"]' },
    { title: 'Choose a mode', body: 'Engine = RoundMaster pairs everyone; Matchmaking = on-demand sessions; Manual = upload pairings. Switch between them to see the settings change.', selector: '[data-tour="tts-mode"]' },
    { title: 'Mode settings', body: 'For Engine, set Swiss vs single-elimination, the number of rounds, top cut, best-of and standings visibility. Matchmaking shows its own queue settings instead.', selector: '[data-tour="tts-engine"]' },
    { title: 'Recruit staff (optional)', body: 'Flip on the Job Board to advertise the roles you need — staff can then apply from the board.', selector: '[data-tour="tts-jobboard"]' },
    { title: 'Create it', body: 'Submit, and you land in the runner for the mode you picked, ready to register players.', selector: '[data-tour="tts-submit"]' },
  ],
});
const CREATE_TOUR = CREATE_PAIR.real;

/** All page tours, in priority order (first match wins). */
export const PAGE_TOURS: PageTour[] = [
  DASHBOARD,
  TUTORIAL_TABLE,
  TABLE_MANAGEMENT,
  TUTORIAL_DECKCHECK,
  DECK_CHECK,
  TIMER_PAIR.sandbox,
  TIMER_OVERVIEW,
  JOBS_PAIR.sandbox,
  JOB_BOARD_TOUR,
  DQ_PAIR.sandbox,
  DQ_CASES_TOUR,
  PENALTY_PAIR.sandbox,
  PENALTY_TOUR,
  FEEDBACK_PAIR.sandbox,
  FEEDBACK_TOUR,
  ROUNDARCHIVE_PAIR.sandbox,
  ROUNDARCHIVE_TOUR,
  FLOORTEAMS_PAIR.sandbox,
  FLOOR_TEAMS,
  SERIES_PAIR.sandbox,
  SERIES_TOUR,
  CREATE_PAIR.sandbox,
  CREATE_TOUR,
  APPEALS_PAIR.sandbox,
  APPEALS,
  STAFFDASH_PAIR.sandbox,
  STAFF_DASH_TOUR,
  TOURNSTAFF_PAIR.sandbox,
  TOURN_STAFF_TOUR,
  JOBAPPS_PAIR.sandbox,
  JOB_APPS_TOUR,
  STOREMGR_PAIR.sandbox,
  STORE_MGR_TOUR,
  PLAYER_TOUR,
  MATCHMAKING_PAIR.sandbox,
  MATCHMAKING_TOUR,
  RUNNER_PAIR.sandbox,
  RUNNER_TOUR,
];

/** The page tour whose `match` accepts this path, if any. */
export function pageTourForPath(path: string): PageTour | null {
  return PAGE_TOURS.find((t) => t.match(path)) || null;
}

/** Look up a page tour by id. */
export function pageTourById(id: string): PageTour | null {
  return PAGE_TOURS.find((t) => t.id === id) || null;
}
